import type { WorkspaceMigrationProfile } from './workspaceProfile'

/**
 * White Label Settings Types
 * 
 * Defines the structure for workspace-level white labeling configuration
 * allowing customers to customize branding, colors, and footer content.
 */

export interface FooterLink {
  label: string
  url: string
}

export interface WhiteLabelSettings {
  enabled: boolean
  customerLogo: string
  companyName: string
  primaryColor: string
  secondaryColor: string
  accentColor: string
  footerText: string
  footerLinks: FooterLink[]
}

export interface WorkspaceSettings {
  whiteLabel?: WhiteLabelSettings
  workspaceProfile?: WorkspaceMigrationProfile
  workspaceContextNotes?: string
}

/**
 * Default white label settings (Ignis branding)
 */
export const DEFAULT_WHITE_LABEL_SETTINGS: WhiteLabelSettings = {
  enabled: false,
  customerLogo: '',
  companyName: 'Ignis',
  primaryColor: '#667eea',
  secondaryColor: '#764ba2',
  accentColor: '#667eea',
  footerText: '© 2025 Ignis by Tenarai',
  footerLinks: [
    { label: 'Privacy', url: '#' },
    { label: 'Terms', url: '#' },
    { label: 'Contact', url: '#' },
    { label: 'Careers', url: '#' },
  ],
}

/**
 * Validates a hex color code
 */
export function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)
}

/**
 * Validates white label settings
 */
export function validateWhiteLabelSettings(settings: Partial<WhiteLabelSettings>): string[] {
  const errors: string[] = []

  if (settings.primaryColor && !isValidHexColor(settings.primaryColor)) {
    errors.push('Primary color must be a valid hex color code')
  }

  if (settings.secondaryColor && !isValidHexColor(settings.secondaryColor)) {
    errors.push('Secondary color must be a valid hex color code')
  }

  if (settings.accentColor && !isValidHexColor(settings.accentColor)) {
    errors.push('Accent color must be a valid hex color code')
  }

  if (settings.customerLogo && settings.customerLogo.length > 0) {
    try {
      new URL(settings.customerLogo)
    } catch {
      errors.push('Customer logo must be a valid URL')
    }
  }

  return errors
}
